import React, { useEffect, useState } from "react";
import api from "../../api/axios";
import ModelSelect from "../ui/ModelSelect";
import LineChart from "../ui/LineChart";

const formatNumber = (value) => new Intl.NumberFormat("fr-FR", {
  maximumFractionDigits: 2,
}).format(Number(value) || 0);

const planningValueLabels = {
  id: "planningValueLabels",
  afterDatasetsDraw(chart) {
    const datasetIndex = chart.data.datasets.findIndex(
      (dataset) => dataset.label === "Production planning"
    );
    if (datasetIndex < 0) return;

    const context = chart.ctx;
    const metadata = chart.getDatasetMeta(datasetIndex);
    context.save();
    context.fillStyle = "#991b1b";
    context.font = "600 11px sans-serif";
    context.textAlign = "center";
    context.textBaseline = "bottom";
    metadata.data.forEach((point, index) => {
      const value = chart.data.datasets[datasetIndex].data[index];
      if (Number.isFinite(Number(value))) {
        context.fillText(formatNumber(value), point.x, point.y - 8);
      }
    });
    context.restore();
  },
};

const CoherenceGammePlanning = () => {
  const [models, setModels] = useState([]);
  const [chains, setChains] = useState([]);
  const [selectedModel, setSelectedModel] = useState("");
  const [selectedChain, setSelectedChain] = useState("");
  const [planning, setPlanning] = useState(null);
  const [planningHours, setPlanningHours] = useState(0);
  const [dailyPlanning, setDailyPlanning] = useState({});
  const [dailyPlanningHours, setDailyPlanningHours] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchModels = async () => {
      try {
        const response = await api.get("/models");
        setModels(response.data);
      } catch (requestError) {
        setError("Impossible de charger les modèles.");
      }
    };

    fetchModels();
  }, []);

  useEffect(() => {
    if (!selectedModel) {
      setChains([]);
      return;
    }

    const fetchChains = async () => {
      try {
        const response = await api.get(`/product_plans_chains/${selectedModel}`);
        setChains(response.data);
      } catch (requestError) {
        setChains([]);
        setError("Impossible de charger les chaînes planifiées.");
      }
    };

    fetchChains();
  }, [selectedModel]);

  useEffect(() => {
    if (!selectedModel || !selectedChain) {
      setPlanning(null);
      setPlanningHours(0);
      setDailyPlanning({});
      setDailyPlanningHours({});
      return;
    }

    const fetchPlanning = async () => {
      setLoading(true);
      setError("");
      try {
        const [planningResponse, dailyResponse] = await Promise.all([
          api.get(`/product_plans_model/${selectedModel}/${selectedChain}`),
          api.get(`/product_plans_model_dash_chain/${selectedModel}/${selectedChain}`),
        ]);
        const hoursByDate = dailyResponse.data?.planned_hours_by_date || {};
        setPlanning(planningResponse.data);
        setDailyPlanning(dailyResponse.data?.planning || {});
        setDailyPlanningHours(hoursByDate);
        setPlanningHours(
          Object.values(hoursByDate).reduce((total, hours) => total + (Number(hours) || 0), 0)
        );
      } catch (requestError) {
        setPlanning(null);
        setPlanningHours(0);
        setDailyPlanning({});
        setDailyPlanningHours({});
        setError(
          requestError.response?.data?.message
          || "Impossible de charger les données de planification."
        );
      } finally {
        setLoading(false);
      }
    };

    fetchPlanning();
  }, [selectedModel, selectedChain]);

  const handleModelChange = (event) => {
    setSelectedModel(event.target.value);
    setSelectedChain("");
    setPlanning(null);
    setPlanningHours(0);
    setDailyPlanning({});
    setDailyPlanningHours({});
    setError("");
  };

  const metrics = planning ? [
    ["Quantité de pièces", planning.qte],
    ["Valeur de travail (VT)", planning.temps_alloue, " min/pièce"],
    ["Effectif prévu dans la gamme", planning.main_oeuvre_directe_gamme],
    ["Effectif total affecté à la chaîne", planning.total_main_oeuvre_directe_affectee],
    ["Personnel complémentaire hors VT", planning.main_oeuvre_directe_support],
  ] : [];
  const productiveMinutes = (Number(planning?.qte) || 0) * (Number(planning?.temps_alloue) || 0);
  const availableMinutes = planningHours * (Number(planning?.main_oeuvre_directe_gamme) || 0) * 60;
  const planningEfficiency = availableMinutes > 0
    ? (productiveMinutes / availableMinutes) * 100
    : 0;
  const targetEfficiency = Number(planning?.rendement_prevu_gamme) || 0;
  const efficiencyGap = planningEfficiency - targetEfficiency;
  const allocatedTime = Number(planning?.temps_alloue) || 0;
  const plannedWorkforce = Number(planning?.main_oeuvre_directe_gamme) || 0;
  const targetProduction = allocatedTime > 0
    ? ((plannedWorkforce * 60) / allocatedTime) * (targetEfficiency / 100)
    : 0;
  const averagePlanningProduction = allocatedTime > 0
    ? ((plannedWorkforce * 60) / allocatedTime) * (planningEfficiency / 100)
    : 0;
  const totalAssignedWorkforce = Number(planning?.total_main_oeuvre_directe_affectee) || 0;
  const totalAvailableMinutes = planningHours * totalAssignedWorkforce * 60;
  const globalEfficiency = totalAvailableMinutes > 0
    ? (productiveMinutes / totalAvailableMinutes) * 100
    : 0;
  const indicators = [
    ["Rendement cible gamme", `${formatNumber(targetEfficiency)}%`],
    ["Rendement résultant du planning", `${formatNumber(planningEfficiency)}%`],
    ["Écart rendement gamme vs planning", `${formatNumber(efficiencyGap)} points`],
    ["Production cible d'après la gamme", `${formatNumber(targetProduction)} pièces/heure`],
    ["Production moyenne d'après planning", `${formatNumber(averagePlanningProduction)} pièces/heure`],
    ["Rendement global avec total effectif affecté", `${formatNumber(globalEfficiency)}%`],
  ];
  const getDailyPlannedPieces = (date) => {
    const plannedValue = dailyPlanning[date];
    return Array.isArray(plannedValue)
      ? plannedValue.reduce((total, entry) => total + (Number(entry.models_finished) || 0), 0)
      : Number(plannedValue) || 0;
  };
  const planningDates = Array.from(new Set([
    ...Object.keys(dailyPlanning),
    ...Object.keys(dailyPlanningHours),
  ])).sort().filter((date) => {
    const isSunday = new Date(`${date}T00:00:00`).getDay() === 0;
    return !isSunday || getDailyPlannedPieces(date) !== 0;
  });
  const dailyAverageProduction = planningDates.map((date) => {
    const plannedPieces = getDailyPlannedPieces(date);
    const hours = Number(dailyPlanningHours[date]) || 0;

    return hours > 0 ? Math.round(plannedPieces / hours) : 0;
  });
  const cadenceChartData = {
    labels: planningDates.map((_, index) => `J${index + 1}`),
    datasets: [
      {
        label: "Production cible d'après la gamme",
        data: planningDates.map(() => Math.round(targetProduction)),
        borderColor: "#2563eb",
        backgroundColor: "#2563eb",
        pointRadius: 0,
        pointHoverRadius: 4,
        tension: 0,
      },
      {
        label: "Production planning",
        data: dailyAverageProduction,
        borderColor: "#dc2626",
        backgroundColor: "#dc2626",
        pointBorderColor: "#dc2626",
        pointBackgroundColor: "#ffffff",
        tension: 0.35,
      },
    ],
  };
  const cadenceChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: { top: 18 } },
    plugins: {
      legend: {
        position: "bottom",
      },
      tooltip: {
        filter: (context) => context.dataset.label === "Production planning",
        callbacks: {
          title: (items) => {
            const index = items[0]?.dataIndex;
            return index === undefined ? "" : `${items[0].label} — ${planningDates[index]}`;
          },
          label: (context) => `${context.dataset.label}: ${Math.round(context.parsed.y)} pièces/heure`,
        },
      },
    },
    scales: {
      x: {
        grid: { color: "rgba(148, 163, 184, 0.35)", borderDash: [4, 4] },
      },
      y: {
        beginAtZero: true,
        title: { display: true, text: "Pièces / heure" },
        grid: { color: "rgba(148, 163, 184, 0.35)", borderDash: [4, 4] },
      },
    },
  };
  const model = models.find((item) => String(item.id) === String(selectedModel));
  const chain = chains.find((item) => String(item.id) === String(selectedChain));

  return (
    <section className="coherence-planning min-w-0 space-y-4 sm:space-y-6">
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <h2 className="text-xl font-bold leading-tight text-gray-900 sm:text-2xl">Cohérence Gamme Planning</h2>
          <p className="mt-1 text-sm text-gray-600 sm:text-base">
            Indicateurs de la gamme de production
          </p>
        </div>

        <div className="flex w-full flex-col gap-4 sm:w-auto sm:flex-row">
          <div className="w-full sm:w-64">
            <label className="mb-2 block text-sm font-medium text-gray-700">
              Sélectionner un modèle
            </label>
            <ModelSelect
              fullWidth
              value={selectedModel}
              onChange={handleModelChange}
              models={models}
              placeholder="Choisir un modèle..."
            />
          </div>

          <div className="w-full sm:w-64">
            <label className="mb-2 block text-sm font-medium text-gray-700">
              Sélectionner une chaîne de production
            </label>
            <select
              className="w-full rounded-lg border border-gray-300 bg-white px-4 py-3 shadow-sm transition-colors duration-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
              value={selectedChain}
              onChange={(event) => setSelectedChain(event.target.value)}
              disabled={!selectedModel}
            >
              <option value="">
                {selectedModel ? "Choisir une chaîne..." : "Choisissez d'abord un modèle"}
              </option>
              {chains.map((chain) => (
                <option key={chain.id} value={chain.id}>{chain.name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {loading && <p className="rounded-lg bg-gray-50 p-6 text-center text-gray-600">Chargement...</p>}

      {!loading && planning && (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg">
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
            <div className="flex items-center space-x-4">
              {model?.image && (
                <img
                  src={`https://api.crosscheckconf.net${model.image}`}
                  alt={model.modele}
                  className="h-16 w-16 rounded-lg border-2 border-white object-cover shadow-md"
                />
              )}
              <div className="text-white">
                <h3 className="text-xl font-bold">{model?.modele}</h3>
                <p className="text-blue-100">{model?.client}</p>
                <p className="text-sm text-blue-200">{chain?.name}</p>
                {planning.date_etude && (
                  <p className="mt-1 text-xs text-blue-100">
                    Gamme enregistrée le {planning.date_etude}
                  </p>
                )}
              </div>
            </div>
          </div>
          <div className="grid min-w-0 grid-cols-1 gap-4 bg-slate-50 p-3 sm:gap-6 sm:p-6">
            <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
              <h4 className="border-b border-slate-200 border-l-4 border-l-blue-600 bg-gradient-to-r from-blue-50 to-slate-50 px-5 py-3 text-base font-bold text-slate-900">
                Informations de production
              </h4>
              <div className="space-y-1 p-3 text-xs text-slate-900 sm:p-5 sm:text-sm">
                {metrics.map(([label, value, suffix = ""], index) => (
                  <div
                    key={label}
                    className={`grid min-w-0 grid-cols-[minmax(0,1fr)_minmax(0,42%)] gap-2 rounded-lg px-2 py-2.5 sm:px-3 ${index % 2 === 0 ? "bg-slate-50" : "bg-white"}`}
                  >
                    <span className="min-w-0 break-words font-medium text-slate-600">{label}</span>
                    <span className="min-w-0 break-words text-right font-bold text-slate-900">
                      {formatNumber(value)}{suffix}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
              <h4 className="border-b border-slate-200 border-l-4 border-l-blue-600 bg-gradient-to-r from-blue-50 to-slate-50 px-5 py-3 text-base font-bold text-slate-900">
                Indicateurs principaux
              </h4>
              <div className="p-3 text-xs text-slate-900 sm:p-5 sm:text-sm">
                <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,42%)] gap-2 rounded-t-lg border-b-2 border-black bg-slate-100 px-2 py-2.5 font-semibold text-slate-950 sm:px-3">
                  <span>KPI</span>
                  <span className="text-right">Valeur</span>
                </div>
                {indicators.map(([label, value], index) => (
                  <div
                    key={label}
                    className={`grid min-w-0 grid-cols-[minmax(0,1fr)_minmax(0,42%)] gap-2 border-b border-slate-200 px-2 py-2.5 sm:px-3 ${index % 2 === 0 ? "bg-white" : "bg-slate-50"}`}
                  >
                    <span className="min-w-0 break-words font-medium text-slate-600">{label}</span>
                    <span className="min-w-0 break-words text-right font-bold text-blue-700">{value}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
              <h4 className="border-b border-slate-200 border-l-4 border-l-blue-600 bg-gradient-to-r from-blue-50 to-slate-50 px-5 py-3 text-base font-bold text-slate-900">
                Seuils de contrôle
              </h4>
              <div className="space-y-3 p-5 text-sm font-semibold text-slate-800">
                <div className="flex items-start gap-3">
                  <span className="mt-1 h-3 w-3 shrink-0 rounded-full bg-emerald-500 shadow-sm" />
                  <span>Écart ≤ 5 points : cohérent</span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="mt-1 h-3 w-3 shrink-0 rounded-full bg-orange-500 shadow-sm" />
                  <span>Écart &gt; 5 et ≤ 10 points : à surveiller / justification demandée</span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="mt-1 h-3 w-3 shrink-0 rounded-full bg-red-500 shadow-sm" />
                  <span>Écart &gt; 10 points : incohérence importante – validation Méthode obligatoire</span>
                </div>
              </div>
            </div>

            <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
              <h4 className="border-b border-slate-200 border-l-4 border-l-blue-600 bg-gradient-to-r from-blue-50 to-slate-50 px-5 py-3 text-base font-bold text-slate-900">
                Courbe de montée en cadence
              </h4>
              <div className="p-3 sm:p-5">
                {planningDates.length > 0 ? (
                  <div className="cadence-chart-content min-w-0 w-full">
                    <LineChart
                      data={cadenceChartData}
                      options={cadenceChartOptions}
                      plugins={[planningValueLabels]}
                    />
                    </div>
                ) : (
                  <p className="py-10 text-center text-sm text-slate-500">
                    Aucune donnée journalière de planification disponible.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {!loading && !planning && !error && (
        <div className="rounded-xl border border-gray-200 bg-white p-10 text-center text-gray-600 shadow-lg">
          Sélectionnez un modèle et une chaîne pour afficher les données de cohérence.
        </div>
      )}
    </section>
  );
};

export default CoherenceGammePlanning;
